import { translate } from '@portal/services/i18n';
import { keepOnlyAlphaNumeric } from '@portal/utils/format';
import React, { useEffect, useState, useRef } from 'react';

import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { TokenMethod } from '@portal/enum/device';

export interface SmsCodeProps {
  code: string;
  setCode: (code: string) => void;
  onClickResend: (method?: TokenMethod) => void;
  className?: string;
  autoFocus?: boolean;
  method?: TokenMethod;
  testId?: string;
}

const SmsCode = ({
  code,
  setCode,
  onClickResend,
  className,
  autoFocus = true,
  method = TokenMethod.SMS,
  testId = '',
}: SmsCodeProps) => {
  const [count, setCount] = useState(60);
  const [resendAvailable, setResendAvailable] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (!resendAvailable) {
      intervalRef.current = setInterval(() => {
        setCount((prevCount) => {
          if (prevCount <= 1) {
            setResendAvailable(true);
            return 60;
          }
          return prevCount - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [resendAvailable]);

  useEffect(() => {
    if (code.length > 6) {
      setCode(code.substring(-1, code.length - 1));
    }
  }, [code, setCode]);

  const handleResend = () => {
    if (resendAvailable) {
      setCount(60);
      setResendAvailable(false);
      onClickResend(method);
    }
  };

  const isWhatsApp = method === TokenMethod.WHATSAPP;
  const methodKey = isWhatsApp ? 'WHATSAPP' : 'SMS';

  return (
    <div className={`sms-code ${className}`}>
      <div className="sms-code__input" data-sentry-mask>
        <AdvancedInput
          testId={testId}
          label={translate(
            `PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.CODE.LABEL_${methodKey}`
          )}
          onChange={(code) => setCode(keepOnlyAlphaNumeric(code.toUpperCase()))}
          value={code}
          autoFocus={autoFocus}
        />
      </div>
      <div className="sms-code__resend-container">
        <ThemedText
          variant="bodySmall"
          color="gray"
          className="sms-code__didnt-receive"
        >
          {translate(
            'PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.CODE.DIDNT_RECEIVE'
          )}
        </ThemedText>
        {resendAvailable ? (
          <button
            type="button"
            className="sms-code__counter--clickable"
            onClick={handleResend}
            aria-label={translate(
              `PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.CODE.RESEND_BY_${methodKey}`
            )}
          >
            <ThemedText
              variant="bodySmall"
              color="primary"
              fontWeight="medium"
              underline
              className="sms-code__counter--text"
            >
              {translate(
                `PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.CODE.RESEND_BY_${methodKey}`
              )}
            </ThemedText>
          </button>
        ) : (
          <ThemedText
            variant="bodySmall"
            color="gray"
            fontWeight="medium"
            className="sms-code__timer"
            aria-live="polite"
            aria-atomic="true"
          >
            {`${count} ${translate(
              'PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.CODE.SECONDS'
            )}`}
          </ThemedText>
        )}
      </div>
    </div>
  );
};

export default SmsCode;
